import { useState, useEffect } from 'react'
import { RiUserAddLine, RiUserUnfollowLine, RiShieldUserLine } from 'react-icons/ri'
import { HiCheck } from 'react-icons/hi2'
import { FaUsers, FaRegBuilding, FaCrown } from 'react-icons/fa'
import { Button } from '../../../../components/Button'
import { BaseModal } from '../../../../components/Modal'
import { UserMultiSelect } from '../../../../components/Select/UserMultiSelect'
import type { Setor, User } from '../../../../models'
import { getSetorType } from '../../../../models'
import type { UserOption } from '../../../../components/Select/types'
import { UserAvatar } from '../../../../components/UserAvatar'
import { toast } from '../../../../components/Toast'
import { apiService } from '../../../demands/services/api'

interface ManageUsersModalProps {
  isOpen: boolean
  setor: Setor | null
  allUsers: User[]
  onClose: () => void
  onVincularUsuarios: (setorId: number, usuariosIds: number[]) => Promise<void>
  onDesvincularUsuario: (usuarioId: number, setorId: number) => Promise<void>
  onRefresh: () => void
}

type UserFunction = 'USUARIO' | 'GESTOR'

interface UserWithPivot extends User {
  pivot?: {
    funcao: UserFunction
    active: boolean
  }
}

export const ManageUsersModal = ({
  isOpen,
  setor,
  allUsers,
  onClose,
  onVincularUsuarios,
  onDesvincularUsuario,
  onRefresh,
}: ManageUsersModalProps) => {
  const [selectedUserIds, setSelectedUserIds] = useState<(string | number)[]>([])
  const [isVinculando, setIsVinculando] = useState(false)
  const [userBeingRemoved, setUserBeingRemoved] = useState<number | null>(null)
  const [selectedForRemoval, setSelectedForRemoval] = useState<Set<number>>(new Set())
  const [isRemovingBulk, setIsRemovingBulk] = useState(false)
  const [userFunctionBeingChanged, setUserFunctionBeingChanged] = useState<number | null>(null)

  // Resetar estado quando modal abrir/fechar
  useEffect(() => {
    if (isOpen) {
      setSelectedUserIds([])
      setUserBeingRemoved(null)
      setSelectedForRemoval(new Set())
    }
  }, [isOpen])

  if (!isOpen || !setor) return null

  // Usuários já vinculados ao setor
  const usuariosVinculados = (setor.usuarios_ativos ||
    setor.usuariosAtivos ||
    setor.usuarios ||
    []) as UserWithPivot[]

  // Usuários disponíveis para vincular (não estão no setor e não são o chefe)
  const usuariosDisponiveis = allUsers.filter(
    user =>
      !usuariosVinculados.some(vinculado => vinculado.id === user.id) &&
      user.id !== setor.chefe_setor_fk
  )

  // Preparar opções para o UserMultiSelect
  const userOptions: UserOption[] = usuariosDisponiveis.map(user => ({
    value: user.id,
    label: user.name,
    name: user.name,
    email: user.email,
    ...(user.avatar && { avatar: user.avatar }),
  }))

  const handleVincularUsuarios = async () => {
    if (selectedUserIds.length === 0) {
      toast.warn('Selecione pelo menos um usuário para vincular')
      return
    }

    try {
      setIsVinculando(true)
      const usuariosIds = selectedUserIds.map(id => Number(id))
      await onVincularUsuarios(setor.id, usuariosIds)

      toast.success(`${selectedUserIds.length} usuário(s) vinculado(s) com sucesso!`)
      setSelectedUserIds([])
      onRefresh()
    } catch (error) {
      console.error('Erro ao vincular usuários:', error)
      toast.error('Erro ao vincular usuários. Tente novamente.')
    } finally {
      setIsVinculando(false)
    }
  }

  const handleDesvincularUsuario = async (usuario: User) => {
    try {
      setUserBeingRemoved(usuario.id)
      await onDesvincularUsuario(usuario.id, setor.id)

      toast.success(
        `${usuario.name} foi desvinculado ${getSetorType(setor) === 'equipe' ? 'da equipe' : 'do setor'}`
      )
      onRefresh()
    } catch (error) {
      console.error('Erro ao desvincular usuário:', error)
      toast.error('Erro ao desvincular usuário. Tente novamente.')
    } finally {
      setUserBeingRemoved(null)
    }
  }

  const handleToggleFuncaoUsuario = async (usuario: UserWithPivot) => {
    try {
      setUserFunctionBeingChanged(usuario.id)

      const currentFuncao = usuario.pivot?.funcao || 'USUARIO'
      const newFuncao: UserFunction = currentFuncao === 'GESTOR' ? 'USUARIO' : 'GESTOR'

      await apiService.atualizarFuncaoUsuario(setor.id, usuario.id, newFuncao)

      toast.success(`${usuario.name} agora é ${newFuncao}`)
      onRefresh()
    } catch (error) {
      console.error('Erro ao alterar função do usuário:', error)
      toast.error('Erro ao alterar função. Tente novamente.')
    } finally {
      setUserFunctionBeingChanged(null)
    }
  }

  const handleToggleUserSelection = (userId: number) => {
    const newSelection = new Set(selectedForRemoval)
    if (newSelection.has(userId)) {
      newSelection.delete(userId)
    } else {
      newSelection.add(userId)
    }
    setSelectedForRemoval(newSelection)
  }

  const handleSelectAllUsers = () => {
    if (selectedForRemoval.size === usuariosVinculados.length) {
      setSelectedForRemoval(new Set())
    } else {
      setSelectedForRemoval(new Set(usuariosVinculados.map(u => u.id)))
    }
  }

  const handleBulkRemoval = async () => {
    if (selectedForRemoval.size === 0) {
      toast.warn('Selecione pelo menos um usuário para remover')
      return
    }

    try {
      setIsRemovingBulk(true)

      // Remover cada usuário selecionado
      for (const userId of selectedForRemoval) {
        await onDesvincularUsuario(userId, setor.id)
      }

      toast.success(`${selectedForRemoval.size} usuário(s) removido(s) com sucesso!`)
      setSelectedForRemoval(new Set())
      onRefresh()
    } catch (error) {
      console.error('Erro ao remover usuários em massa:', error)
      toast.error('Erro ao remover usuários. Tente novamente.')
    } finally {
      setIsRemovingBulk(false)
    }
  }

  const entityType = getSetorType(setor)

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Gerenciar Usuários - ${setor.nome_setor}`}
      size='xl'
      resizable={true}
      icon={
        entityType === 'equipe' ? (
          <FaUsers className='w-6 h-6 text-purple-600' />
        ) : (
          <FaRegBuilding className='w-6 h-6 text-blue-600' />
        )
      }
      description={`Adicione ou remova usuários ${entityType === 'equipe' ? 'da equipe' : 'do setor'}`}
      useStandardLayout={true}
      className='min-h-[700px] max-h-[90vh]'
    >
      <div className='p-6 h-full flex flex-col gap-6'>
        {/* Informações do Chefe - TOPO COM DESTAQUE */}
        {setor.chefe && (
          <div className='p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-lg shadow-sm'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='flex-shrink-0'>
                  <div className='relative'>
                    <UserAvatar user={setor.chefe} size='md' />
                    <div className='absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1 shadow-md'>
                      <FaCrown className='w-3 h-3 text-yellow-700' />
                    </div>
                  </div>
                </div>
                <div>
                  <div className='flex items-center gap-2'>
                    <h4 className='font-bold text-blue-900 dark:text-blue-100'>
                      {setor.chefe.name}
                    </h4>
                    <span className='px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full'>
                      CHEFE
                    </span>
                  </div>
                  <p className='text-sm text-blue-700 dark:text-blue-300'>
                    {setor.chefe.cargo_usuario}
                  </p>
                  {setor.chefe.email && (
                    <p className='text-xs text-blue-600 dark:text-blue-400'>{setor.chefe.email}</p>
                  )}
                </div>
              </div>
              <div className='text-right'>
                <p className='text-xs text-blue-600 dark:text-blue-400 font-medium'>
                  Chefe do {entityType}
                </p>
                <p className='text-xs text-blue-500 dark:text-blue-500 mt-1'>
                  Automaticamente vinculado
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Grid Principal */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0'>
          {/* Coluna 1: Adicionar Usuários */}
          <div className='space-y-4 flex flex-col min-h-0 bg-green-50/50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800'>
            <div className='flex items-center gap-2'>
              <RiUserAddLine className='w-5 h-5 text-green-600' />
              <h3 className='font-semibold text-gray-900 dark:text-gray-100'>Adicionar Usuários</h3>
            </div>

            {usuariosDisponiveis.length > 0 ? (
              <>
                <UserMultiSelect
                  variant='user-multi'
                  options={userOptions}
                  value={selectedUserIds}
                  onChange={setSelectedUserIds}
                  placeholder='Selecione usuários para adicionar...'
                  searchable={true}
                  maxDisplayItems={3}
                  className='mb-4'
                />

                <Button
                  onClick={handleVincularUsuarios}
                  disabled={selectedUserIds.length === 0 || isVinculando}
                  className='w-full bg-green-600 hover:bg-green-700'
                >
                  {isVinculando ? 'Vinculando...' : `Vincular ${selectedUserIds.length} usuário(s)`}
                </Button>
              </>
            ) : (
              <div className='text-center py-8 bg-white dark:bg-neutral-800 rounded-lg border border-dashed border-gray-300 dark:border-neutral-600'>
                <RiUserAddLine className='w-8 h-8 text-gray-400 mx-auto mb-2' />
                <p className='text-gray-600 dark:text-gray-400 text-sm'>
                  Todos os usuários disponíveis já estão vinculados ao {entityType}
                </p>
              </div>
            )}
          </div>

          {/* Coluna 2: Usuários Vinculados */}
          <div className='space-y-4 flex flex-col min-h-0 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg p-4 border border-blue-200 dark:border-blue-800'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <FaUsers className='w-5 h-5 text-blue-600' />
                <h3 className='font-semibold text-gray-900'>
                  Usuários Vinculados ({usuariosVinculados.length})
                </h3>
              </div>

              {usuariosVinculados.length > 1 && (
                <div className='flex items-center gap-2'>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleSelectAllUsers}
                    className='text-blue-600 hover:text-blue-700 text-xs'
                  >
                    {selectedForRemoval.size === usuariosVinculados.length
                      ? 'Desmarcar todos'
                      : 'Selecionar todos'}
                  </Button>

                  {selectedForRemoval.size > 0 && (
                    <Button
                      variant='destructive'
                      size='sm'
                      onClick={handleBulkRemoval}
                      disabled={isRemovingBulk}
                      className='text-xs'
                    >
                      {isRemovingBulk
                        ? 'Removendo...'
                        : `Remover ${selectedForRemoval.size} usuário(s)`}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {usuariosVinculados.length > 0 ? (
              <div className='space-y-2 flex-1 overflow-y-auto pr-2'>
                {usuariosVinculados.map(usuario => {
                  const isSelected = selectedForRemoval.has(usuario.id)
                  const funcao = usuario.pivot?.funcao || 'USUARIO'
                  const isGestor = funcao === 'GESTOR'
                  const isChefe = usuario.id === setor.chefe_setor_fk
                  
                  // Determinar o label e estilo do botão
                  let funcaoLabel = 'MEMBRO'
                  let funcaoNextLabel = 'SUPERVISOR'
                  let funcaoStyle = 'bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-600'
                  
                  if (isChefe) {
                    funcaoLabel = 'GESTOR'
                    funcaoStyle = 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 cursor-not-allowed opacity-75'
                  } else if (isGestor) {
                    funcaoLabel = 'SUPERVISOR'
                    funcaoNextLabel = 'MEMBRO'
                    funcaoStyle = 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/60'
                  }

                  return (
                    <div
                      key={usuario.id}
                      className={`relative flex items-center gap-3 p-3 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-400 dark:border-blue-600 shadow-sm'
                          : 'bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 border border-gray-200 dark:border-neutral-600'
                      }`}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={() => handleToggleUserSelection(usuario.id)}
                        className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${
                          isSelected
                            ? 'bg-blue-600 border-blue-600 scale-110'
                            : 'border-gray-300 dark:border-neutral-500 hover:border-blue-400'
                        }`}
                      >
                        {isSelected && <HiCheck className='h-4 w-4 text-white' />}
                      </button>

                      {/* User Info */}
                      <div className='flex items-center gap-3 flex-1 min-w-0'>
                        <UserAvatar user={usuario} size='sm' />
                        <div className='flex-1 min-w-0'>
                          <p className='font-medium text-gray-900 dark:text-gray-100 truncate'>
                            {usuario.name}
                          </p>
                          <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                            {usuario.cargo_usuario}
                          </p>
                        </div>
                      </div>

                      {/* Função Toggle */}
                      <button
                        onClick={() => !isChefe && handleToggleFuncaoUsuario(usuario)}
                        disabled={userFunctionBeingChanged === usuario.id || isChefe}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold transition-all ${funcaoStyle}`}
                        title={isChefe ? 'Chefe do setor - não pode ser alterado' : `Clique para alterar para ${funcaoNextLabel}`}
                      >
                        {userFunctionBeingChanged === usuario.id ? (
                          <div className='w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin' />
                        ) : (
                          <>
                            <RiShieldUserLine className='w-3 h-3' />
                            {funcaoLabel}
                          </>
                        )}
                      </button>

                      {/* Individual Remove Button */}
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={e => {
                          e.stopPropagation()
                          handleDesvincularUsuario(usuario)
                        }}
                        disabled={userBeingRemoved === usuario.id || isRemovingBulk}
                        className='text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 p-1'
                        title={`Desvincular ${usuario.name}`}
                      >
                        {userBeingRemoved === usuario.id ? (
                          <div className='w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin' />
                        ) : (
                          <RiUserUnfollowLine className='w-4 h-4' />
                        )}
                      </Button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className='text-center py-8 bg-white dark:bg-neutral-800 rounded-lg border border-dashed border-gray-300 dark:border-neutral-600'>
                <FaUsers className='w-8 h-8 text-gray-400 mx-auto mb-2' />
                <p className='text-gray-600 dark:text-gray-400 text-sm'>
                  Nenhum usuário vinculado ao {entityType}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legenda */}
      <div className='flex flex-wrap items-center justify-center gap-4 p-3 mx-6 bg-gray-50 dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 text-xs'>
        <div className='flex items-center gap-2'>
          <div className='px-2 py-0.5 bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 rounded-full font-semibold'>
            MEMBRO
          </div>
          <span className='text-gray-600 dark:text-gray-400'>Usuário padrão</span>
        </div>
        <div className='flex items-center gap-2'>
          <div className='px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full font-semibold'>
            SUPERVISOR
          </div>
          <span className='text-gray-600 dark:text-gray-400'>Com permissões elevadas</span>
        </div>
        <div className='flex items-center gap-2'>
          <div className='px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full font-semibold'>
            GESTOR
          </div>
          <span className='text-gray-600 dark:text-gray-400'>Chefe do setor (fixo)</span>
        </div>
        <div className='flex items-center gap-2'>
          <RiUserUnfollowLine className='w-4 h-4 text-red-600' />
          <span className='text-gray-600 dark:text-gray-400'>Remover usuário</span>
        </div>
      </div>
    </BaseModal>
  )
}
